import { useTranslation } from 'react-i18next'
import React, { useEffect, useCallback, useState, useRef, useMemo } from 'react'

import {
  formatPhoneNumberForSave,
  isValidUsPhoneDigits,
} from '../../../lib/phone'
import { isValidEin, normalizeEin } from '../../../lib/charitySearch'
import { restoreState } from '../../../lib/localstorage'
import { ensureAbsoluteUrl } from '../../../utils/url'
import { sortObjectsAlphabetically } from '../../../lib/sort'
import { useCurrentUser } from '../../../hooks/useCurrentUser'
import useApi from '../../../hooks/useApi'
import { buildCauseOptions, currentLang } from '../../account/settings/options'
import {
  hardNormalizeSlug,
  RESERVED_BUSINESS_SLUGS,
  softNormalizeSlug,
} from './businessSlug'
import { MAX_BUSINESS_ADMINS } from './constants'

export default function useBusinessEdit() {
  const { t, i18n } = useTranslation()

  const { currentUser, locale, hasBetaFeature } = useCurrentUser()
  const localeKey = locale === 'es' ? 'es_name' : 'en_name'
  const { business_id } = currentUser || {}

  const {
    getBusiness,
    getIndustries,
    getEventCategories,
    updateBusiness,
    businessSlugAvailable,
    addBusinessAdmin,
    removeBusinessAdmin,
  } = useApi()

  const [hasBusinessChanged, setHasBusinessChanged] = useState(false)
  const [businessLoading, setBusinessLoading] = useState(false)
  const [business, setBusiness] = useState()
  const [savedEin, setSavedEin] = useState('')
  const [industries, setIndustries] = useState(() =>
    restoreState('industries', []),
  )
  const [categoriesList, setCategoriesList] = useState([])
  const [isAdminModalOpen, setIsAdminModalOpen] = useState(false)
  const [businessAdmins, setBusinessAdmins] = useState([])
  const [businessAdminsInitial, setBusinessAdminsInitial] = useState([])
  const [hasSocialNetworksChanged, setHasSocialNetworksChanged] =
    useState(false)
  const [hasMediaChanged, setHasMediaChanged] = useState(false)
  const [hasMessageChanged, setHasMessageChanged] = useState(false)
  const [hasAdminsChanged, setHasAdminsChanged] = useState(false)
  const [isSavingSocials, setIsSavingSocials] = useState(false)
  const [error, setError] = useState(null)
  const [initialSlug, setInitialSlug] = useState('')
  const [checkingSlug, setCheckingSlug] = useState(false)
  const [slugTaken, setSlugTaken] = useState(false)
  const [slugReserved, setSlugReserved] = useState(false)
  const slugCheckTimer = useRef(null)

  const fetchData = useCallback(async () => {
    if (!business_id) return
    if (business?.id) return

    setBusinessLoading(true)
    try {
      const { data } = await getBusiness(business_id)
      if (data) {
        const categoryIds = data.categories?.map(c => c.id) ?? []
        setBusiness({
          ...data,
          industry_id: data.industry?.id,
          category_ids: categoryIds,
          ein: normalizeEin(data.ein),
        })
        setSavedEin(normalizeEin(data.ein))
        setInitialSlug(data.slug || '')
        if (data.business_admins !== undefined) {
          const admins = data.business_admins || []
          setBusinessAdmins(admins)
          setBusinessAdminsInitial(admins)
        }
      }

      const [{ data: _industries }, { data: _categories }] = await Promise.all([
        getIndustries(),
        getEventCategories(),
      ])
      if (_industries) {
        setIndustries(sortObjectsAlphabetically(_industries, localeKey))
      }
      if (_categories) {
        setCategoriesList(_categories)
      }
    } catch (err) {
      console.error('Error fetching business data:', err)
      setError(t('business.edit.save_failed_retry'))
    } finally {
      setBusinessLoading(false)
    }
  }, [
    getBusiness,
    getIndustries,
    getEventCategories,
    business_id,
    localeKey,
    t,
    business?.id,
  ])

  const onSaveBusiness = useCallback(async () => {
    if (!business) return

    const normalizedSlug = hardNormalizeSlug(business.slug)
    const effectiveSlug = normalizedSlug || initialSlug
    const slugChanged = effectiveSlug !== initialSlug

    if (
      slugChanged &&
      (!effectiveSlug ||
        checkingSlug ||
        slugTaken ||
        slugReserved ||
        RESERVED_BUSINESS_SLUGS.includes(effectiveSlug))
    ) {
      setError(t('business.edit.slug_invalid'))
      return
    }

    const einDigits = normalizeEin(business.ein)
    if (einDigits && !isValidEin(einDigits)) {
      setError(t('business.edit.ein_invalid', 'EIN must be exactly 9 digits.'))
      return
    }

    if (business.phone && !isValidUsPhoneDigits(business.phone)) {
      setError(
        t('business.edit.phone_invalid', 'Enter a 10-digit US phone number.'),
      )
      return
    }

    setBusinessLoading(true)
    setError(null)

    const params = {
      business: {
        address_line_1: business.address_line_1,
        address_line_2: business.address_line_2,
        category_ids: business.category_ids,
        city: business.city,
        ein: einDigits || '',
        industry_id: business.industry_id,
        name: business.name,
        phone: business.phone ? formatPhoneNumberForSave(business.phone) : '',
        slug: effectiveSlug,
        state: business.state,
        website_url: ensureAbsoluteUrl(business.website_url) || '',
        zip: business.zip,
        facebook_link: ensureAbsoluteUrl(business.facebook_link) || '',
        linkedin_link: ensureAbsoluteUrl(business.linkedin_link) || '',
        instagram_link: ensureAbsoluteUrl(business.instagram_link) || '',
        youtube_link: ensureAbsoluteUrl(business.youtube_link) || '',
        tiktok_link: ensureAbsoluteUrl(business.tiktok_link) || '',
        community_statement: business.community_statement,
      },
      cover_photo_id: business.cover_photo?.id || business.cover_photo_id,
      banner_ad_graphics_id:
        business.banner_ad_graphics?.id || business.banner_ad_graphics_id,
    }

    try {
      const response = await updateBusiness(business_id, params)
      if (response.errors) {
        setError(
          response.errors?.full_messages?.[0] || t('business.edit.save_failed'),
        )
      } else {
        setError(null)
        if (response.data) {
          const nextSlug = response.data.slug || effectiveSlug
          const nextEin = normalizeEin(response.data.ein ?? einDigits)
          setBusiness(prev => ({
            ...prev,
            ...response.data,
            industry_id: response.data.industry?.id,
            category_ids:
              response.data.categories?.map(c => c.id) ??
              prev.category_ids ??
              [],
            ein: nextEin,
            cover_photo:
              response.data.cover_photo !== undefined
                ? response.data.cover_photo
                : prev.cover_photo,
            cover_photo_id:
              response.data.cover_photo_id !== undefined
                ? response.data.cover_photo_id
                : prev.cover_photo_id,
            banner_ad_graphics:
              response.data.banner_ad_graphics !== undefined
                ? response.data.banner_ad_graphics
                : prev.banner_ad_graphics,
            banner_ad_graphics_id:
              response.data.banner_ad_graphics_id !== undefined
                ? response.data.banner_ad_graphics_id
                : prev.banner_ad_graphics_id,
          }))
          setSavedEin(nextEin)
          setInitialSlug(nextSlug)
          setSlugTaken(false)
          setSlugReserved(false)
          if (response.data.business_admins !== undefined) {
            const admins = response.data.business_admins || []
            setBusinessAdmins(admins)
            setBusinessAdminsInitial(admins)
          }
        } else {
          setSavedEin(einDigits)
        }
        setHasBusinessChanged(false)
        setHasSocialNetworksChanged(false)
        setHasMediaChanged(false)
        setHasMessageChanged(false)
      }
    } catch (err) {
      setError(t('business.edit.save_failed_retry'))
    } finally {
      setBusinessLoading(false)
    }
  }, [
    business,
    business_id,
    updateBusiness,
    t,
    initialSlug,
    slugReserved,
    slugTaken,
    checkingSlug,
  ])

  useEffect(() => {
    fetchData().catch(err => {
      console.error('Error fetching business data:', err)
      setError(t('business.edit.save_failed_retry'))
      setBusinessLoading(false)
    })
  }, [fetchData, t])

  useEffect(() => {
    return () => {
      if (slugCheckTimer.current) {
        clearTimeout(slugCheckTimer.current)
      }
    }
  }, [])

  const onChangeSlug = useCallback(
    value => {
      const softValue = softNormalizeSlug(value)
      setBusiness(prev => {
        if (!prev) {
          if (business_id) {
            return { id: business_id, slug: softValue }
          }
          return prev
        }
        return { ...prev, slug: softValue }
      })
      setHasBusinessChanged(true)

      if (slugCheckTimer.current) {
        clearTimeout(slugCheckTimer.current)
      }

      setCheckingSlug(false)
      setSlugTaken(false)
      setSlugReserved(false)

      const normalized = hardNormalizeSlug(softValue)
      if (!normalized || normalized === initialSlug) {
        return
      }

      if (RESERVED_BUSINESS_SLUGS.includes(normalized)) {
        setSlugReserved(true)
        return
      }

      slugCheckTimer.current = setTimeout(async () => {
        setCheckingSlug(true)
        try {
          const response = await businessSlugAvailable({
            slug: normalized,
            businessId: business_id,
          })
          const available = !!response?.data?.available
          setSlugTaken(!available)
        } catch (err) {
          setSlugTaken(true)
        } finally {
          setCheckingSlug(false)
        }
      }, 500)
    },
    [business_id, businessSlugAvailable, initialSlug],
  )

  const updateBusinessField = useCallback(
    (field, value) => {
      setBusiness(prev => {
        if (!prev) {
          if (business_id) {
            return { id: business_id, [field]: value }
          }
          return prev
        }
        return { ...prev, [field]: value }
      })
      setHasBusinessChanged(true)
    },
    [business_id],
  )

  const updateSocialField = useCallback((field, value) => {
    setBusiness(prev => ({ ...prev, [field]: value }))
    setHasSocialNetworksChanged(true)
    setHasBusinessChanged(true)
  }, [])

  const imageBusinessOnChange = async mediaAttachment => {
    const response = await updateBusiness(business_id, {
      media_attachment_id: mediaAttachment.id,
      business: null,
    })
    if (response.errors) {
      setError(
        response.errors?.full_messages?.[0] ||
          t('business.edit.profile_picture_failed'),
      )
    } else {
      setBusiness(prev => ({
        ...prev,
        media_attachment: { ...mediaAttachment },
      }))
    }
  }

  const handleCoverPhotoChange = useCallback(mediaAttachment => {
    setBusiness(prev => ({
      ...prev,
      cover_photo: mediaAttachment,
      cover_photo_id: mediaAttachment?.id,
    }))
    setHasMediaChanged(true)
    setHasBusinessChanged(true)
  }, [])

  const handleBannerAdGraphicsChange = useCallback(mediaAttachment => {
    setBusiness(prev => ({
      ...prev,
      banner_ad_graphics: mediaAttachment,
      banner_ad_graphics_id: mediaAttachment?.id,
    }))
    setHasMediaChanged(true)
    setHasBusinessChanged(true)
  }, [])

  const handleAddAdmin = useCallback(admin => {
    setBusinessAdmins(prev => {
      if (
        prev.length < MAX_BUSINESS_ADMINS &&
        !prev.find(a => a.id === admin.id)
      ) {
        setHasAdminsChanged(true)
        return [...prev, admin]
      }
      return prev
    })
  }, [])

  const handleRemoveAdmin = useCallback(admin => {
    setBusinessAdmins(prev => {
      setHasAdminsChanged(true)
      return prev.filter(a => a.id !== admin.id)
    })
  }, [])

  const onSaveAdmins = useCallback(async () => {
    if (!business_id) return

    setBusinessLoading(true)
    setError(null)

    try {
      const adminsToAdd = businessAdmins.filter(
        a => !businessAdminsInitial.find(initial => initial.id === a.id),
      )
      const adminsToRemove = businessAdminsInitial.filter(
        initial => !businessAdmins.find(current => current.id === initial.id),
      )

      if (adminsToAdd.length > 0 || adminsToRemove.length > 0) {
        if (adminsToAdd.length) {
          await Promise.all(
            adminsToAdd.map(admin =>
              addBusinessAdmin({ businessId: business_id, userId: admin.id }),
            ),
          )
        }

        if (adminsToRemove.length) {
          await Promise.all(
            adminsToRemove.map(admin =>
              removeBusinessAdmin({
                businessId: business_id,
                userId: admin.id,
              }),
            ),
          )
        }

        const { data: refreshedBusiness } = await getBusiness(business_id)
        if (refreshedBusiness?.business_admins !== undefined) {
          setBusinessAdmins(refreshedBusiness.business_admins || [])
          setBusinessAdminsInitial(refreshedBusiness.business_admins || [])
        }

        setHasAdminsChanged(false)
      }
    } catch (err) {
      setError(t('business.edit.save_failed_retry'))
    } finally {
      setBusinessLoading(false)
    }
  }, [
    business_id,
    businessAdmins,
    businessAdminsInitial,
    addBusinessAdmin,
    removeBusinessAdmin,
    getBusiness,
    t,
  ])

  const onSaveSocials = useCallback(async () => {
    setIsSavingSocials(true)
    try {
      await onSaveBusiness()
    } finally {
      setIsSavingSocials(false)
    }
  }, [onSaveBusiness])

  const saveCauses = useCallback(
    async ids => {
      if (!business?.id) return false
      const previous = business.category_ids ?? []
      setBusiness(prev => ({ ...prev, category_ids: ids }))
      try {
        const response = await updateBusiness(business.id, {
          business: { category_ids: ids },
        })
        if (response?.errors) {
          setBusiness(prev => ({ ...prev, category_ids: previous }))
          // eslint-disable-next-line no-alert
          alert(
            response.errors?.full_messages?.[0] ||
              t('business.edit.save_failed'),
          )
          return false
        }
        if (response.data) {
          setBusiness(prev => ({
            ...prev,
            ...response.data,
            industry_id: response.data.industry?.id ?? prev.industry_id,
            category_ids: response.data.categories?.map(c => c.id) ?? ids,
          }))
        }
        return true
      } catch (err) {
        setBusiness(prev => ({ ...prev, category_ids: previous }))
        // eslint-disable-next-line no-alert
        alert(t('business.edit.save_failed_retry'))
        return false
      }
    },
    [business, updateBusiness, t],
  )

  const lang = currentLang(i18n.language)
  const causeOptions = useMemo(
    () => buildCauseOptions(categoriesList, lang),
    [categoriesList, lang],
  )

  const profileSaveDisabled = useMemo(() => {
    if (!business) return true

    const normalizedSlugValue = hardNormalizeSlug(business.slug)
    const slugBlocksSave =
      checkingSlug ||
      slugTaken ||
      slugReserved ||
      (!normalizedSlugValue && !!initialSlug)

    const einDigits = normalizeEin(business.ein)
    const einBlocksSave = einDigits.length > 0 && !isValidEin(einDigits)
    const phoneBlocksSave =
      !!business.phone && !isValidUsPhoneDigits(business.phone)

    return (
      !hasBusinessChanged || slugBlocksSave || einBlocksSave || phoneBlocksSave
    )
  }, [
    business,
    hasBusinessChanged,
    checkingSlug,
    slugTaken,
    slugReserved,
    initialSlug,
  ])

  const breadcrumbs = useMemo(() => {
    if (!business) return []
    return [
      { label: t('navigation_banner.home'), to: '/' },
      {
        label: business.name || t('navigation_banner.business_profile'),
        to: business.slug
          ? `/profile/business/${encodeURIComponent(
              hardNormalizeSlug(business.slug),
            )}`
          : undefined,
      },
      { label: t('business.edit.title') },
    ]
  }, [business, t])

  const retryFetch = useCallback(() => {
    setError(null)
    setBusinessLoading(true)
    fetchData().catch(err => {
      console.error('Error fetching business data:', err)
      setError(t('business.edit.save_failed_retry'))
      setBusinessLoading(false)
    })
  }, [fetchData, t])

  const pageStatus = useMemo(() => {
    if (!business_id) return 'no_business'
    if (!business && businessLoading) return 'initial_loading'
    if (!business && error) return 'load_error'
    if (!business) return 'initial_loading'
    return 'ready'
  }, [business_id, business, businessLoading, error])

  return {
    pageStatus,
    business_id,
    business,
    setBusiness,
    savedEin,
    industries,
    localeKey,
    locale,
    hasBetaFeature,
    businessLoading,
    error,
    setError,
    businessAdmins,
    isAdminModalOpen,
    setIsAdminModalOpen,
    causeOptions,
    breadcrumbs,
    profileSaveDisabled,
    initialSlug,
    checkingSlug,
    slugTaken,
    slugReserved,
    hasSocialNetworksChanged,
    hasMediaChanged,
    hasMessageChanged,
    hasAdminsChanged,
    isSavingSocials,
    hardNormalizeSlug,
    fetchData: retryFetch,
    onSaveBusiness,
    onChangeSlug,
    updateBusinessField,
    updateSocialField,
    imageBusinessOnChange,
    handleCoverPhotoChange,
    handleBannerAdGraphicsChange,
    handleAddAdmin,
    handleRemoveAdmin,
    onSaveAdmins,
    onSaveSocials,
    saveCauses,
    setHasMessageChanged,
    updateBusiness,
    t,
  }
}
